import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { LOGO_URL } from '../../mock';

export default function LoginPage() {
  const { admin, setupNeeded, login, loading } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (setupNeeded) return <Navigate to="/admin/setup" replace />;
  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0d12] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute -top-40 -left-32 w-[500px] h-[500px] rounded-full bg-[#E5614C]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#5DCAB5]/12 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-white flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(229,97,76,0.6)] overflow-hidden">
            <img src={LOGO_URL} alt="GEMS" className="h-full w-full object-contain p-1.5" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-display text-[22px] font-bold">
              GEMS <span className="text-[#E5614C] italic">@</span> The Arts
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">Admin CMS</div>
          </div>
        </div>

        <div className="bg-[#16131a] border border-white/[0.06] rounded-[24px] p-8 lg:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
          <h1 className="font-serif-display text-[28px] font-bold">Welcome back</h1>
          <p className="text-[14px] text-white/55 mt-1">Sign in to manage your site.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-white/80 text-[13px] font-semibold">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="email" type="email" autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@gemsatthearts.org"
                  className="h-12 pl-10 rounded-xl bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw" className="text-white/80 text-[13px] font-semibold">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="pw" type="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 pl-10 rounded-xl bg-[#0f0d12] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                  required
                />
              </div>
            </div>
            <Button
              type="submit" disabled={busy}
              className="w-full h-12 rounded-xl bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold py-3 mt-2 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center text-[12px] text-white/40 flex items-center justify-center gap-1.5">
          <Sparkles className="h-3 w-3 text-[#E5614C]" />
          Custom CMS for GEMS @ The Arts
        </div>
      </div>
    </div>
  );
}
